import axios from 'axios';
import {
  addTranslationForLanguage,
  initialize,
  setActiveLanguage,
} from 'react-localize-redux';
import * as Redux from 'redux';

import { ASSETS_BASE_URL, BUILD_NUM } from '../../BuildConstants';
import { LocationCertainty } from '../../core/api/general/general.types';
import { Menu } from '../../core/api/menu/menu.types';
import {
  getCollectionSummaries,
  getDeliverySummaries,
} from '../../core/api/restaurant/restaurant.service';
import {
  GetCollectionSummariesQueryParams,
  GetDeliverySummariesQueryParams,
  RestaurantSummaryCommon,
} from '../../core/api/restaurant/restaurant.types';
import { logger } from '../../core/services/loggerService';
import { SetMenu } from '../../core/state/menu/menu.types';
import { setMenu } from '../../core/state/menu/menuActions';
import {
  loadCollectionRestaurantDetail,
  loadDeliveryRestaurantDetail,
} from '../../core/state/restaurant/restaurantMiddleware';
import { Async, Status } from '../../core/types/data.types';
import { Routes } from '../../core/types/route.types';
import {
  availableLanguages,
  DEFAULT_LOCALE_CODE,
  formatLanguageJSON,
  translateTransform,
} from '../../localization';
import DEFAULT_TRANSLATION from '../../localization/en.json';
import { sortData } from '../../utils/dataUtils';
import * as Data from '../Data';
import { storage } from '../services/storageServices';
import * as Store from '../Store';
import { Utilities } from '../Utilities';
import { AddLocation, SetCountryCode } from './Actions';

interface IpInfo {
  ip: string;
  hostname: string;
  city: string;
  region: string;
  country: string;
  loc: string;
  org: string;
}

const applyCallbackToMenu = (
  data: RestaurantSummaryCommon[],
  callbackFn?: (vrid: number) => void,
) => {
  if (data.length === 0) {
    return;
  }

  if (callbackFn) {
    const vrid = data[0].VirtualRestaurantId;
    callbackFn(vrid);
  }
};

// ! Deprecated - use custom hook useRestaurantSummaries to get summaries from src/core/hooks/fetch/useRestaurantSummaries/index.ts
// TODO - ORD-59 remove legacy action FindInitialRestaurant
export const FindInitialRestaurant =
  (then?: (vrid: number) => any, _iteration?: number) =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const { flipdish } = getState();

    const isPickupEnabled =
      flipdish.AppConfig.status === 'success' &&
      flipdish.AppConfig.data.PickupEnabled;
    if (isPickupEnabled) {
      const queryParams: GetCollectionSummariesQueryParams = {
        count: 1000,
        latitude: flipdish.Location.geo.Latitude,
        longitude: flipdish.Location.geo.Longitude,
        skip: 0,
      };

      const includePhysicalRestaurantIds =
        flipdish.Config.IncludePhysicalRestaurantIds;
      if (includePhysicalRestaurantIds) {
        queryParams.includePhysicalRestaurantIds = includePhysicalRestaurantIds;
      }

      await getCollectionSummaries(queryParams)
        .then((data) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'success',
              data,
            },
          });
          applyCallbackToMenu(data as RestaurantSummaryCommon[], then);
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }

    const isDeliveryEnabled =
      flipdish.AppConfig.status === 'success' &&
      flipdish.AppConfig.data.DeliveryEnabled;
    if (isDeliveryEnabled) {
      const queryParams: GetDeliverySummariesQueryParams = {
        count: 1000,
        deviceId: flipdish.Config.DeviceId,
        latitude: flipdish.Location.geo.Latitude,
        longitude: flipdish.Location.geo.Longitude,
        skip: 0,
      };

      await getDeliverySummaries(queryParams)
        .then((data) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'success',
              data,
            },
          });

          applyCallbackToMenu(data as RestaurantSummaryCommon[], then);
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }
  };

// TODO - ORD-59 refactor setupLanguage and move to src/core/state/general/generalMiddleware.ts
export const setupLanguage =
  (languageToSet: string) => async (dispatch: Redux.Dispatch<Redux.Action>) => {
    let locale = DEFAULT_LOCALE_CODE;
    let translatedJson;
    let language = languageToSet;
    // Override language choice if customer has set the language
    // manually from change language.
    const customerLanguage = Utilities.getLangauageFromStorage();
    if (customerLanguage && customerLanguage !== language) {
      language = customerLanguage;
    }
    if (language === DEFAULT_LOCALE_CODE) {
      translatedJson = DEFAULT_TRANSLATION;
    } else {
      try {
        const languages = [language];
        const [languagePrefix] = language.split('-');
        const similarLanguage = availableLanguages.find(
          (l) => l.startsWith(languagePrefix) && l !== language,
        );
        if (similarLanguage) {
          languages.push(similarLanguage);
        }
        const responses = await Promise.all(
          languages.map(async (l) => {
            if (l === DEFAULT_LOCALE_CODE) {
              return DEFAULT_TRANSLATION;
            }
            const response = await fetch(
              // @ts-ignore its set in main.js
              `${ASSETS_BASE_URL}static/languages/${l}.json?version=${BUILD_NUM}`,
            );
            return response.json();
          }),
        );
        const formattedLanguage = formatLanguageJSON(responses);
        locale = language;
        translatedJson = formattedLanguage;
      } catch (e) {
        logger.error(
          `Something went wrong while downloading translations for ${language}`,
          {
            message: e.message,
            stack: e.stack,
          },
        );
        locale = DEFAULT_LOCALE_CODE;
        translatedJson = DEFAULT_TRANSLATION;
        // Remove user language from local storage if
        // there is error in loading language.
        if (customerLanguage) {
          Utilities.removeLangauageFromStorage();
        }
      }
    }
    dispatch(initialize([locale]));
    dispatch(
      addTranslationForLanguage(translateTransform(translatedJson), locale),
    );
    dispatch(setActiveLanguage(locale));
    axios.defaults.headers.common['Flipdish-Language'] = locale;
  };

// ! Deprecated - Static Menu only needs this. Remove when Static Menu functionality is removed
export const LoadRestaurant = (props: Routes) => {
  const { params } = props;

  const vrId = parseInt(`${params.vrid}`);
  // params.id can equal physicalRestaurantId and deliveryLocationId depending on the route
  const prId = params.id ? parseInt(`${params.id}`) : undefined;
  const deliveryLocationId = prId;
  const deliveryType = params.type;

  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchThunk = dispatch as any;

    const { CollectionSummaries, DeliverySummaries } = getState().flipdish;

    let Summary: RestaurantSummaryCommon;
    switch (deliveryType) {
      case 'collection': {
        if (CollectionSummaries.status === 'success' && prId) {
          Summary = CollectionSummaries.data.find(
            (s) =>
              s.VirtualRestaurantId === vrId && s.PhysicalRestaurantId === prId,
          );

          dispatchThunk(loadCollectionRestaurantDetail(prId));
        }
        break;
      }
      case 'delivery': {
        if (DeliverySummaries.status === 'success' && deliveryLocationId) {
          Summary = DeliverySummaries.data.find(
            (s) => s.VirtualRestaurantId === vrId,
          );

          dispatchThunk(loadDeliveryRestaurantDetail(vrId, deliveryLocationId));
        }
        break;
      }
      default:
        // Horrible roundabout way of retrieving a restaurant for Static Menus / Menu Preview where there is URL route {appId}/menu#/menu/{vrId}
        const getSummary = (summaries: Data.Async<RestaurantSummaryCommon[]>) =>
          summaries.status === 'success' &&
          summaries.data.find((s) => s.VirtualRestaurantId === vrId);

        if (
          CollectionSummaries.status === 'success' &&
          CollectionSummaries.data.length > 0
        ) {
          Summary = getSummary(CollectionSummaries);

          dispatchThunk(
            loadCollectionRestaurantDetail(Summary?.PhysicalRestaurantId),
          );
        } else if (DeliverySummaries.status === 'success') {
          Summary = getSummary(DeliverySummaries);

          dispatchThunk(loadDeliveryRestaurantDetail(vrId, deliveryLocationId));
        }
        break;
    }

    // If summary load menu
    if (Summary) {
      const loadMenu = (
        RestaurantSummaries: RestaurantSummaryCommon[],
      ): void => {
        const dispatchSetMenu = (menu: Async<Menu>, menuUrl): SetMenu =>
          dispatch(setMenu(menu, menuUrl));

        const ApiRequestMenuUrls = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const RestaurantSummary of RestaurantSummaries) {
          const { menus } = getState().menu;
          const existingMenuData = menus[RestaurantSummary.MenuUrl];

          // If menu doesn't exist in the redux store dispatch pending and get menu from localStorage if it exists
          if (!existingMenuData) {
            dispatchSetMenu(
              { status: Status.LOADING },
              RestaurantSummary.MenuUrl,
            );

            const menuJson = storage.getItem<Menu>(RestaurantSummary.MenuUrl);

            if (menuJson) {
              dispatchSetMenu(
                { status: Status.SUCCESS, data: menuJson },
                RestaurantSummary.MenuUrl,
              );
            }
          }

          // Check for if the menu data in the redux store has been got from the API
          if (existingMenuData && existingMenuData.status === 'success') {
            // eslint-disable-next-line no-continue
            continue;
          }

          // When loading menus check that API call is not made twice for the same menu
          if (
            ApiRequestMenuUrls.findIndex(
              (ApiRequestMenuUrl) =>
                ApiRequestMenuUrl === RestaurantSummary.MenuUrl,
            ) !== -1
          ) {
            // eslint-disable-next-line no-continue
            continue;
          }

          ApiRequestMenuUrls.push(RestaurantSummary.MenuUrl);

          axios({
            method: 'get',
            url: RestaurantSummary.MenuUrl,
            withCredentials: false,
          })
            .then((result) => {
              const menu: Menu = result.data;

              // Arrange sections by DisplayOrder
              menu.MenuSections = sortData(menu.MenuSections);

              dispatchSetMenu(
                { status: Status.SUCCESS, data: menu },
                RestaurantSummary.MenuUrl,
              );
            })
            .catch((_error) => {
              setTimeout(() => {
                FindInitialRestaurant(() => {}, 2);
              }, 5000);
            });
        }
      };

      loadMenu([Summary]);
    }
  };
};

// TODO - ORD-59 refactor LoadGeoIpLookup and move to src/core/api/user/user.service.ts & src/core/state/user/userMiddleware.ts
export const LoadGeoIpLookup =
  () => async (dispatch: Redux.Dispatch<Redux.Action>) => {
    const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';
    const isOperaMini = navigator.userAgent.indexOf('Opera Mini') > -1;
    if (isDev || isOperaMini) {
      return;
    }

    const commonHeaders = { ...axios.defaults.headers.common };
    delete commonHeaders['X-Flipdish-Token'];
    delete commonHeaders['X-Coordinates'];
    delete commonHeaders['Flipdish-App-Type'];
    delete commonHeaders['Flipdish-White-Label-Id'];

    await axios
      .get('/json', {
        baseURL: 'https://ipinfo.io',
        headers: {
          common: commonHeaders,
        },
        withCredentials: false,
      })
      .then((data) => {
        const result: IpInfo = data.data;
        const [lat, lng] = result.loc.split(',').map(parseFloat);

        const geo = { Latitude: lat, Longitude: lng };

        dispatch(SetCountryCode(result.country));
        dispatch(AddLocation(geo, LocationCertainty.GeoIp));
      })
      .catch(() => {
        console.log('Error looking up ip address on ipinfo.io');
      });
  };
